/**
 * Communications settings hook — reads/writes the DB-backed per-channel settings
 * (provider selection, retry/webhook/rate-limit) and the write-only credential
 * store. Secret credential values are never returned by the API (only `isSet`).
 */

import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import { toast } from '../../../../components/ui/providers/AlertProvider';

export const CHANNEL_IDS: Record<'email' | 'sms' | 'whatsapp' | 'push', number> = {
  email: 0,
  sms: 1,
  whatsapp: 2,
  push: 3,
};

export interface ProviderOption {
  providerId: number;
  code: string;
  name: string;
  description?: string | null;
}

export interface CredentialStatus {
  providerId: number;
  keyName: string;
  isSet: boolean;
  isSecret: boolean;
  /** Plaintext value for non-secret config; null for secrets (use revealCredential). */
  value?: string | null;
}

export interface ChannelSettingView {
  channelId: number;
  channelName: string;
  defaultProviderId?: number | null;
  defaultProviderCode?: string | null;
  isEnabled: boolean;
  maxRetryCount?: number | null;
  retryBackoffSeconds?: number | null;
  webhookUrl?: string | null;
  rateLimitPerMinute?: number | null;
  providers: ProviderOption[];
  credentials: CredentialStatus[];
}

export interface UpdateChannelSettingRequest {
  defaultProviderId?: number | null;
  isEnabled?: boolean;
  maxRetryCount?: number | null;
  retryBackoffSeconds?: number | null;
  webhookUrl?: string | null;
  rateLimitPerMinute?: number | null;
}

const path = (p: string) => getApiUrl('communications', `/api/communication-settings${p}`);

export function useCommunicationSettings() {
  const httpClient = useHttpClient();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<ChannelSettingView | null>(null);

  const fetchSettings = useCallback(
    async (channelId: number) => {
      setLoading(true);
      setError(null);
      try {
        const resp = await httpClient.get<ChannelSettingView>(path(`/${channelId}`));
        setSettings((resp.data as ChannelSettingView) ?? null);
      } catch (err: any) {
        setError(err?.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    },
    [httpClient],
  );

  const updateSettings = useCallback(
    async (channelId: number, body: UpdateChannelSettingRequest) => {
      setSaving(true);
      try {
        const resp = await httpClient.put<ChannelSettingView>(path(`/${channelId}`), body);
        setSettings((resp.data as ChannelSettingView) ?? null);
        toast.success('Settings saved');
        return true;
      } catch (err: any) {
        toast.error(err?.message || 'Failed to save settings');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [httpClient],
  );

  const setCredential = useCallback(
    async (channelId: number, providerId: number, keyName: string, value: string, isSecret = true) => {
      setSaving(true);
      try {
        await httpClient.put(path(`/${channelId}/credentials`), { providerId, keyName, value, isSecret });
        toast.success(`Credential "${keyName}" saved`);
        await fetchSettings(channelId);
        return true;
      } catch (err: any) {
        toast.error(err?.message || 'Failed to save credential');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [httpClient, fetchSettings],
  );

  const revealCredential = useCallback(
    async (channelId: number, providerId: number, keyName: string): Promise<string | null> => {
      try {
        const resp = await httpClient.get<{ value?: string | null }>(
          path(`/${channelId}/credentials/${providerId}/reveal?keyName=${encodeURIComponent(keyName)}`),
        );
        const data: any = resp.data;
        // Unwrap ResponseData<{ value }> or a bare { value }.
        return (data?.data?.value ?? data?.value ?? null) as string | null;
      } catch (err: any) {
        toast.error(err?.message || 'Failed to reveal value');
        return null;
      }
    },
    [httpClient],
  );

  return { loading, saving, error, settings, fetchSettings, updateSettings, setCredential, revealCredential };
}
